import React, { FC } from 'react';
import { Card, Col, Row } from 'antd';
import {
  faCar,
  faClipboardList,
  faPaperPlane,
  faShip,
  faTruck,
  faWarehouse,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from 'styled-components';
import { Icon } from '@/ui/components';
import { useTranslate } from '@/translations';
import { TranslationKey } from '@/translations/types';
import { useKpiSummary } from '@/api/OperationsDashboard';
import { CardSkeleton, WidgetError } from './WidgetStates';

const CARD_ORDER = [
  'vesselArrivals',
  'vehicleArrivals',
  'stockInCompounds',
  'loadsSubmitted',
  'loadsPulled',
  'vehicleDespatches',
];

const LABEL_KEY: Record<string, TranslationKey> = {
  vesselArrivals: 'dashboard.kpi.vesselArrivals',
  vehicleArrivals: 'dashboard.kpi.vehicleArrivals',
  stockInCompounds: 'dashboard.kpi.stockInCompounds',
  loadsSubmitted: 'dashboard.kpi.loadsSubmitted',
  loadsPulled: 'dashboard.kpi.loadsPulled',
  vehicleDespatches: 'dashboard.kpi.vehicleDespatches',
};

const ICON: Record<string, IconDefinition> = {
  vesselArrivals: faShip,
  vehicleArrivals: faCar,
  stockInCompounds: faWarehouse,
  loadsSubmitted: faClipboardList,
  loadsPulled: faTruck,
  vehicleDespatches: faPaperPlane,
};

export const KpiSummaryRow: FC = () => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { data, isLoading, isError, refetch } = useKpiSummary();

  if (isError) {
    return (
      <Card>
        <WidgetError height={120} onRetry={() => refetch()} />
      </Card>
    );
  }

  const cardsByKey = new Map((data?.cards ?? []).map((c) => [c.key, c]));

  return (
    <Row gutter={16} align="stretch">
      {CARD_ORDER.map((key) => {
        const card = cardsByKey.get(key);
        const label = t(LABEL_KEY[key]);
        return (
          <Col
            key={key}
            xl={4}
            md={8}
            sm={12}
            xs={24}
            style={{ marginBottom: 16, display: 'flex' }}
          >
            <Card style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  minHeight: 36,
                }}
              >
                <Icon icon={ICON[key]} color="#838383" scale={0.95} />
                <div
                  style={{
                    color: theme.textColor,
                    opacity: 0.6,
                    fontSize: 13,
                    lineHeight: 1.3,
                  }}
                >
                  {label}
                </div>
              </div>
              {isLoading || !card ? (
                <CardSkeleton />
              ) : (
                <>
                  <div
                    style={{
                      color: theme.primaryBg,
                      fontSize: 28,
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    {card.current.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {card.variancePct === null ? (
                      <span style={{ color: theme.textColor, opacity: 0.5 }}>
                        {t('dashboard.kpi.noBaseline')}
                      </span>
                    ) : (
                      <span
                        style={{
                          color: card.variancePct >= 0 ? '#00b087' : '#e53935',
                        }}
                      >
                        {card.variancePct >= 0 ? '▲ +' : '▼ '}
                        {card.variancePct}
                        {'% '}
                        <span style={{ color: theme.textColor, opacity: 0.5 }}>
                          {t('dashboard.kpi.vsLastWeek', {
                            value: card.previous.toLocaleString(),
                          })}
                        </span>
                      </span>
                    )}
                  </div>
                </>
              )}
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};
